import { useState } from 'react'
import {
  ArrowLeft, ChevronDown, ChevronRight, CheckCircle2, Calendar,
  Globe, Mail, Settings, Zap, Edit3, RefreshCw, Clock
} from 'lucide-react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import WysiwygEditor from '../../components/WysiwygEditor'
import './AICreateCampaign.css'
import '../Pages.css'

const TIMEZONES = [
  { value: 'UTC',  label: 'UTC — Coordinated Universal Time' },
  { value: 'EST',  label: 'EST — Eastern Standard (US)' },
  { value: 'EDT',  label: 'EDT — Eastern Daylight (US)' },
  { value: 'CST',  label: 'CST — Central Standard (US)' },
  { value: 'CDT',  label: 'CDT — Central Daylight (US)' },
  { value: 'MST',  label: 'MST — Mountain Standard (US)' },
  { value: 'MDT',  label: 'MDT — Mountain Daylight (US)' },
  { value: 'PST',  label: 'PST — Pacific Standard (US)' },
  { value: 'PDT',  label: 'PDT — Pacific Daylight (US)' },
  { value: 'IST',  label: 'IST — India Standard Time' },
  { value: 'GMT',  label: 'GMT — Greenwich Mean Time' },
  { value: 'CET',  label: 'CET — Central European Time' },
  { value: 'CEST', label: 'CEST — Central European Summer' },
  { value: 'JST',  label: 'JST — Japan Standard Time' },
  { value: 'AEST', label: 'AEST — Australian Eastern Standard' },
  { value: 'AEDT', label: 'AEDT — Australian Eastern Daylight' }
]

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const TIMEZONE_OFFSETS = {
  UTC: 0, GMT: 0, EST: -300, EDT: -240, CST: -360, CDT: -300,
  MST: -420, MDT: -360, PST: -480, PDT: -420,
  IST: 330, CET: 60, CEST: 120, JST: 540, AEST: 600, AEDT: 660,
}

const convertToUTC = (timeStr, timezone) => {
  if (!timeStr) return null
  const offset = TIMEZONE_OFFSETS[timezone] || 0
  const [hours, minutes] = timeStr.split(':').map(Number)
  const date = new Date()
  date.setHours(hours, minutes, 0, 0)
  date.setMinutes(date.getMinutes() - offset)
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

const convertFromUTC = (timeStr, timezone) => {
  if (!timeStr) return ''
  const offset = TIMEZONE_OFFSETS[timezone] || 0
  const [hours, minutes] = timeStr.split(':').map(Number)
  const date = new Date()
  date.setHours(hours, minutes, 0, 0)
  date.setMinutes(date.getMinutes() + offset)
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

export default function AIGeneratedPreview({ campaign, onChange, onBack, onCreate, saving }) {
  const [expandedSection, setExpandedSection] = useState('name')
  const [expandedStep, setExpandedStep] = useState(null)

  const toggleSection = (section) => {
    setExpandedSection(prev => prev === section ? null : section)
  }

  const updateName = (value) => {
    onChange({ ...campaign, name: value })
  }

  const updateSchedule = (field, value) => {
    onChange({ ...campaign, schedule: { ...campaign.schedule, [field]: value } })
  }

  const toggleScheduleDay = (day) => {
    const days = campaign.schedule.days || []
    const updated = days.includes(day) ? days.filter(d => d !== day) : [...days, day]
    updateSchedule('days', updated)
  }

  const handleTimezoneChange = (newTz) => {
    const sched = campaign.schedule || {}
    const currentTz = sched.timezone || 'UTC'
    const utcFrom = convertToUTC(sched.fromTime || '09:00', currentTz)
    const utcTo = convertToUTC(sched.toTime || '17:00', currentTz)
    onChange({
      ...campaign,
      schedule: {
        ...sched,
        timezone: newTz,
        fromTime: convertFromUTC(utcFrom, newTz),
        toTime: convertFromUTC(utcTo, newTz),
      }
    })
  }

  const updateOptions = (field, value) => {
    onChange({ ...campaign, options: { ...campaign.options, [field]: value } })
  }

  const updateStep = (stepIdx, variantIdx, field, value) => {
    const steps = campaign.sequence.steps.map((step, si) => {
      if (si !== stepIdx) return step
      return {
        ...step,
        variants: step.variants.map((variant, vi) => {
          if (vi !== variantIdx) return variant
          return { ...variant, [field]: value }
        })
      }
    })
    onChange({ ...campaign, sequence: { ...campaign.sequence, steps } })
  }

  const updateStepDelay = (stepIdx, value) => {
    const steps = campaign.sequence.steps.map((step, si) =>
      si === stepIdx ? { ...step, delay: parseInt(value) || 0 } : step
    )
    onChange({ ...campaign, sequence: { ...campaign.sequence, steps } })
  }

  const { schedule, sequence, options } = campaign
  const steps = sequence?.steps || []

  const summarySchedule = [
    schedule?.days?.length > 0 && `${schedule.days.join(', ')}`,
    schedule?.fromTime && schedule?.toTime && `${schedule.fromTime}–${schedule.toTime} ${schedule.timezone || 'UTC'}`
  ].filter(Boolean).join(' · ')

  return (
    <div className="ai-preview-layout">
      {/* Left: Sticky Summary */}
      <div className="ai-preview-summary">
        <div className="card" style={{ padding: 0 }}>
          <div className="ai-summary-header">
            <div className="ai-summary-title">Campaign Preview</div>
            <div className="ai-summary-name">{campaign.name || 'Untitled Campaign'}</div>
          </div>

          <div className="ai-summary-meta">
            {summarySchedule && (
              <div className="ai-summary-row">
                <Calendar size={13} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
                <span>{summarySchedule}</span>
              </div>
            )}
            <div className="ai-summary-row">
              <Mail size={13} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
              <span>{steps.length} email step{steps.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="ai-summary-row">
              <Zap size={13} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
              <span>{options?.emailsPerDay || 40} emails/day · {options?.delaySeconds || 120}s delay</span>
            </div>
          </div>

          <div className="ai-summary-divider" />

          <div className="ai-summary-actions">
            <button
              onClick={onCreate}
              disabled={saving}
              className="btn btn-ai"
              style={{ justifyContent: 'center' }}
            >
              {saving ? (
                <><RefreshCw size={15} className="animate-spin" /> Creating...</>
              ) : (
                <><CheckCircle2 size={15} /> Create Campaign</>
              )}
            </button>
            <button onClick={onBack} className="btn btn-ghost" style={{ justifyContent: 'center' }}>
              <ArrowLeft size={15} />
              Back
            </button>
          </div>
        </div>
      </div>

      {/* Right: Accordion Sections */}
      <div>
        {/* Section 1: Campaign Name */}
        <div className="ai-accordion-section">
          <div className="ai-accordion-header" onClick={() => toggleSection('name')}>
            <div className="ai-accordion-title">
              <Edit3 size={16} style={{ color: '#6366f1' }} />
              Campaign Name
            </div>
            {expandedSection === 'name'
              ? <ChevronDown size={18} style={{ color: 'var(--color-text-muted)' }} />
              : <ChevronRight size={18} style={{ color: 'var(--color-text-muted)' }} />
            }
          </div>
          {expandedSection === 'name' && (
            <div className="ai-accordion-body">
              <div className="form-group" style={{ marginTop: '1rem' }}>
                <label>Campaign Name</label>
                <input
                  type="text"
                  value={campaign.name || ''}
                  onChange={(e) => updateName(e.target.value)}
                  placeholder="Enter campaign name..."
                />
              </div>
            </div>
          )}
        </div>

        {/* Section 2: Schedule */}
        <div className="ai-accordion-section">
          <div className="ai-accordion-header" onClick={() => toggleSection('schedule')}>
            <div className="ai-accordion-title">
              <Calendar size={16} style={{ color: '#6366f1' }} />
              Schedule
            </div>
            {expandedSection === 'schedule'
              ? <ChevronDown size={18} style={{ color: 'var(--color-text-muted)' }} />
              : <ChevronRight size={18} style={{ color: 'var(--color-text-muted)' }} />
            }
          </div>
          {expandedSection === 'schedule' && (
            <div className="ai-accordion-body">
              <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div className="form-group">
                  <label>Schedule Name</label>
                  <input
                    type="text"
                    value={schedule?.scheduleName || ''}
                    onChange={(e) => updateSchedule('scheduleName', e.target.value)}
                    placeholder="e.g., Weekday Morning Send"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                      <Calendar size={13} /> Start Date
                    </label>
                    <DatePicker
                      selected={schedule?.startDate ? new Date(schedule.startDate) : null}
                      onChange={(date) => updateSchedule('startDate', date ? date.toISOString().split('T')[0] : null)}
                      dateFormat="yyyy-MM-dd"
                      placeholderText="Select start date"
                      isClearable
                      minDate={new Date()}
                      popperProps={{ strategy: 'fixed' }}
                    />
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                      <Calendar size={13} /> End Date
                    </label>
                    <DatePicker
                      selected={schedule?.endDate ? new Date(schedule.endDate) : null}
                      onChange={(date) => updateSchedule('endDate', date ? date.toISOString().split('T')[0] : null)}
                      dateFormat="yyyy-MM-dd"
                      placeholderText="Select end date"
                      isClearable
                      minDate={schedule?.startDate ? new Date(schedule.startDate) : new Date()}
                      popperProps={{ strategy: 'fixed' }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <Globe size={13} /> Timezone
                  </label>
                  <select
                    value={schedule?.timezone || 'UTC'}
                    onChange={(e) => handleTimezoneChange(e.target.value)}
                  >
                    {TIMEZONES.map(tz => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                  </select>
                  <span className="time-hint">Times are stored in UTC and displayed in your selected timezone.</span>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                      <Clock size={13} /> From Time ({schedule?.timezone || 'UTC'})
                    </label>
                    <input
                      type="time"
                      value={schedule?.fromTime || '09:00'}
                      onChange={(e) => updateSchedule('fromTime', e.target.value)}
                    />
                    {schedule?.fromTime && (
                      <span className="time-hint">
                        <Globe size={11} /> UTC: {convertToUTC(schedule.fromTime, schedule?.timezone || 'UTC')}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                      <Clock size={13} /> To Time ({schedule?.timezone || 'UTC'})
                    </label>
                    <input
                      type="time"
                      value={schedule?.toTime || '17:00'}
                      onChange={(e) => updateSchedule('toTime', e.target.value)}
                    />
                    {schedule?.toTime && (
                      <span className="time-hint">
                        <Globe size={11} /> UTC: {convertToUTC(schedule.toTime, schedule?.timezone || 'UTC')}
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '0.75rem' }}>
                    Active Days
                  </label>
                  <div className="day-toggle-grid">
                    {DAYS.map(day => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleScheduleDay(day)}
                        className={`day-toggle-btn${(schedule?.days || []).includes(day) ? ' active' : ''}`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Section 3: Email Sequence */}
        <div className="ai-accordion-section">
          <div className="ai-accordion-header" onClick={() => toggleSection('sequence')}>
            <div className="ai-accordion-title">
              <Mail size={16} style={{ color: '#6366f1' }} />
              Email Sequence ({steps.length} steps)
            </div>
            {expandedSection === 'sequence'
              ? <ChevronDown size={18} style={{ color: 'var(--color-text-muted)' }} />
              : <ChevronRight size={18} style={{ color: 'var(--color-text-muted)' }} />
            }
          </div>
          {expandedSection === 'sequence' && (
            <div className="ai-accordion-body">
              <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {steps.map((step, stepIdx) => {
                  const variant = step.variants?.[0] || {}
                  const isExpanded = expandedStep === stepIdx
                  return (
                    <div key={stepIdx} className="ai-step-card">
                      <div className="ai-step-card-header">
                        <span>Step {stepIdx + 1}{stepIdx > 0 && step.delay > 0 ? ` — ${step.delay}d delay` : ''}</span>
                        <button
                          className="btn btn-ghost"
                          style={{ fontSize: '0.775rem', padding: '0.3rem 0.6rem' }}
                          onClick={() => setExpandedStep(isExpanded ? null : stepIdx)}
                        >
                          <Edit3 size={12} />
                          {isExpanded ? 'Collapse' : 'Edit'}
                        </button>
                      </div>

                      {!isExpanded ? (
                        <div className="ai-step-card-preview">
                          <strong>{variant.subject || '(No subject)'}</strong>
                          <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                            {variant.body
                              ? variant.body.replace(/<[^>]+>/g, '').slice(0, 120) + (variant.body.length > 120 ? '...' : '')
                              : '(No body)'
                            }
                          </span>
                        </div>
                      ) : (
                        <div className="ai-step-card-body">
                          {stepIdx > 0 && (
                            <div className="form-group" style={{ marginBottom: '1rem' }}>
                              <label>Delay (days after previous step)</label>
                              <input
                                type="number"
                                min="1"
                                value={step.delay || 1}
                                onChange={(e) => updateStepDelay(stepIdx, e.target.value)}
                                style={{ maxWidth: '120px' }}
                              />
                            </div>
                          )}
                          <div className="form-group" style={{ marginBottom: '1rem' }}>
                            <label>Subject Line</label>
                            <input
                              type="text"
                              value={variant.subject || ''}
                              onChange={(e) => updateStep(stepIdx, 0, 'subject', e.target.value)}
                              placeholder="Email subject..."
                            />
                          </div>
                          <div className="form-group">
                            <label>Email Body</label>
                            <WysiwygEditor
                              value={variant.body || ''}
                              onChange={(val) => updateStep(stepIdx, 0, 'body', val)}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Section 4: Options */}
        <div className="ai-accordion-section">
          <div className="ai-accordion-header" onClick={() => toggleSection('options')}>
            <div className="ai-accordion-title">
              <Settings size={16} style={{ color: '#6366f1' }} />
              Campaign Options
            </div>
            {expandedSection === 'options'
              ? <ChevronDown size={18} style={{ color: 'var(--color-text-muted)' }} />
              : <ChevronRight size={18} style={{ color: 'var(--color-text-muted)' }} />
            }
          </div>
          {expandedSection === 'options' && (
            <div className="ai-accordion-body">
              <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Sending Speed */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Emails per day</label>
                    <input
                      type="number"
                      min="1"
                      value={options?.emailsPerDay ?? 40}
                      onChange={(e) => updateOptions('emailsPerDay', parseInt(e.target.value))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Delay between emails (seconds)</label>
                    <input
                      type="number"
                      min="0"
                      value={options?.delaySeconds ?? 120}
                      onChange={(e) => updateOptions('delaySeconds', parseInt(e.target.value))}
                    />
                  </div>
                </div>

                {/* Toggles */}
                <div className="toggle-list">
                  {[
                    { key: 'stopOnReply', label: 'Stop sending on reply', desc: 'Automatically stop sequence when recipient replies' },
                    { key: 'openTracking', label: 'Open tracking', desc: 'Track when recipients open your emails' },
                    { key: 'linkTracking', label: 'Link tracking', desc: 'Track when recipients click links' },
                    { key: 'deliveryOptimization', label: 'Delivery optimization', desc: 'Remove tracking pixels to improve inbox placement' },
                    { key: 'sendTextOnly', label: 'Send emails as text-only', desc: 'Send all emails in plain text format' },
                    { key: 'sendFirstEmailTextOnly', label: 'First email text-only', desc: 'Only the first email will be in plain text' }
                  ].map(({ key, label, desc }) => (
                    <div key={key} className="toggle-item">
                      <div className="toggle-info">
                        <div className="toggle-label">{label}</div>
                        <div className="toggle-desc">{desc}</div>
                      </div>
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={options?.[key] ?? false}
                          onChange={() => updateOptions(key, !(options?.[key] ?? false))}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
